<template>
  <v-layout row wrap>
    <v-flex xs12>
      <v-btn
        color="primary"
        class="new-report"
        @click="createReport"
        style="margin-left: 0; margin-right: 0;"
      >
        New Report
      </v-btn>
    </v-flex>

    <v-flex xs12>
      <v-data-table
        class="elevation-1"
        :headers="headers"
        :items="reports"
        :rows-per-page-items="rowsPerPageItems"
        :loading="reportsLoading"
      >
        <template v-slot:items="props">
          <tr 
            style="cursor: pointer;"
            @click="() => goClips(props.item.uuid)"
          >
            <td>{{ props.item.name }}</td>
            <td class="text-xs-center">{{ props.item.clips.length }}</td>
            <td class="text-xs-center">{{ props.item.organization.name }}</td>
            <td class="text-xs-center">{{ props.item.createdAt | parseFormat3 }}</td>
            <td class="text-xs-center">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small @click.stop="() => editReport(props.item)">edit</v-icon>
                </template>
                <span>Edit Report</span>
              </v-tooltip>
              &nbsp;
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small @click.stop="() => removeReport(props.item)">delete</v-icon>
                </template>
                <span>Delete Report</span>
              </v-tooltip>
              &nbsp;
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small @click.stop="() => showGenerateReportForm(props.item)">get_app</v-icon>
                </template>
                <span>Download Report</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>  
    </v-flex>

    <v-dialog v-model="downloadReportLoading" width="200" persistent>
      <v-card>
        <v-card-text class="text-xs-center">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="reportDialog" width="500">
      <v-card>
        <v-card-title class="title">
          Add New Report
        </v-card-title>
        <v-card-text>
          <v-form>
            <v-container>
              <v-text-field 
                v-model="report.name" 
                label="Name"
                data-vv-name="name"
                v-validate="'required'"
                :error-messages="errors.collect('name')"
              ></v-text-field>
              <v-select
                v-model="report.organization"
                :items="organizations"
                :disabled="!(isSuperAdmin || isBindezAdmin)"
                label="Select Organization"
                item-text="displayName"
                item-value="uuid"
                data-vv-name="organization"
                v-validate="'required'"
                :error-messages="errors.collect('organization')"
              ></v-select>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            flat
            @click="cancel"
          >Cancel</v-btn>
          <v-btn
            color="primary"
            flat
            @click="save"
          >Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="generateReportFormDialog" :width="570" persistent>
      <GenerateReportForm 
        :report="generatingReport"
        :show="generateReportFormDialog"
        @cancel="generateReportFormDialog = false" 
        @generate="payload => processReport({reportID: generatingReport.uuid, payload})"
      />
    </v-dialog>
  </v-layout>
</template>

<script>
import { mapActions, mapState } from "vuex";
import current_user from "@/mixins/current_user";
import moment from "moment";
import custom_date from "@/mixins/custom_date";
import reportdownload from "@/mixins/reportdownload";
import Vue from 'vue';
import VeeValidate from 'vee-validate';
import GenerateReportForm from "@/components/GenerateReportForm";

Vue.use(VeeValidate);

const defaultReport = {
  uuid: undefined,
  name: "",
  organization: undefined,
};

export default {
  components: {
    GenerateReportForm,
  },

  mixins: [ current_user, custom_date, reportdownload ],

  $_veeValidate: {
    validator: "new",
  },

  data() {
    return {
      reportDialog: false,

      report: {
        uuid: undefined,
        name: "",
        organization: undefined,
      },

      headers: [
        {
          text: "Name",
          value: "name",
          sortable: false,
        },
        {
          text: "Clips",
          value: "clips",
          sortable: false,
          align: "center"
        },
        {
          text: "Organization",
          value: "organization",
          sortable: false,
          align: "center",
        },
        {
          text: "Created At",
          value: "created_at",
          sortable: false,
          align: "center",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],

      rowsPerPageItems: [ 50 ],

      dictionary: {
        custom: {
          name: {
            required: () => "Name cannot be empty."
          },

          organization: {
            required: () => "Organization must be selected."
          }
        }
      },

      generatingReport: undefined,
    }
  },

  created() {
    this.fetchReports();
  },

  mounted() {
    this.$validator.localize("en", this.dictionary);
  },

  computed: {
    ...mapState("reports", [
      "reports",
      "reportsLoading",
    ]),

    ...mapState("organizations", [
      "organizations",
    ]),
  },

  methods: {
    ...mapActions("reports", [
      "saveReport",
      "fetchReports",
      "deleteReport",
      "updateReport",
      "generateReport",
    ]),

    save() {
      this.$validator.validateAll()
          .then(valid => {
            if (valid) {
              if (this.report.uuid) {
                // update
                this.updateReport(this.report)
                  .then(() => {
                    this.reportDialog = false;
                    this.report = Object.assign({}, defaultReport);
                  });
                this.$ua.trackEvent('UpdateReport', 'click',
                  `${this.currentUser.username || 'User'} updated ${this.report.uuid} with payload ${JSON.stringify(this.report, null, 2)}`);
              } else {
                // save new
                this.saveReport(this.report)
                  .then(() => {
                    this.reportDialog = false;
                    this.report = Object.assign({}, defaultReport);
                  });
                this.$ua.trackEvent('CreateReport', 'click',
                  `${this.currentUser.username || 'User'} created ${JSON.stringify(this.report, null, 2)}`);
              }
            }
          });
    },

    createReport() {
      this.$validator.reset();
      this.report = Object.assign({}, defaultReport, {
        organization: !(this.isSuperAdmin || this.isBindezAdmin) 
          ? this.currentUser.organization.uuid 
          : null
      });
      this.reportDialog = true;
    },

    removeReport(report) {
      if (report.clips.length > 0) {
        alert("Cannot delete if clips are still in this report.");
        return;
      }
      if (confirm("Are you sure this delete this report?")) {
        this.deleteReport(report.uuid);
        this.$ua.trackEvent('DeleteReport', 'click',
          `${this.currentUser.username || 'User'} deleted ${report.uuid}`);
      } 
    },

    editReport(report) {
      this.report = Object.assign({}, {
        uuid: report.uuid,
        name: report.name,
        organization: report.organization.uuid,
      });
      this.reportDialog = true;
    },

    cancel() {
      this.reportDialog = false;
      this.$validator.reset();
      this.report = Object.assign({}, defaultReport);
    },

    goClips(reportID) {
      this.$router.push({ name: "clips", params: { id: reportID }})
    },

    parseDate(rawDate) {
      return moment(rawDate).calendar();
    },

    showGenerateReportForm(report) {
      this.generatingReport = report;
      this.generateReportFormDialog = true;
    }
  }
}
</script>

<style scoped>

</style>